import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API = "/api";

/**
 * ScanPage - QR-Code Aufgaben-Erstellung
 * 
 * KOMPLETT ISOLIERT - KEINE React-Router-Hooks
 */
const ScanPage = () => {
  // Property-ID aus URL manuell extrahieren (NICHT useParams!)
  const getPropertyIdFromUrl = () => {
    const path = window.location.pathname;
    const match = path.match(/\/scan\/([^/]+)/);
    return match ? match[1] : null;
  };
  
  // Prüfen ob Mitarbeiter-Modus (nach Login-Redirect mit ?staff=1)
  // WICHTIG: Reaktiv - wird bei jeder URL-Änderung neu berechnet
  const getStaffMode = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('staff') === '1';
  };
  
  const [propertyId] = useState(getPropertyIdFromUrl);
  const [staffMode, setStaffMode] = useState(getStaffMode); // Initial + reaktiv
  const fileInputRef = useRef(null);
  
  // Staff-Mode reaktiv aktualisieren wenn sich URL ändert (z.B. nach Login-Redirect)
  useEffect(() => {
    const checkStaffMode = () => {
      const newStaffMode = getStaffMode();
      setStaffMode(newStaffMode);
    };
    
    // Prüfe bei popstate (Browser zurück/vorwärts)
    window.addEventListener('popstate', checkStaffMode);
    
    // Initial prüfen
    checkStaffMode();
    
    return () => window.removeEventListener('popstate', checkStaffMode);
  }, []);
  
  // States
  const [pageState, setPageState] = useState('loading'); // loading | choice | form | submitting | success | error
  const [property, setProperty] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isGuestMode, setIsGuestMode] = useState(!getStaffMode()); // Gast-Modus nur wenn NICHT staff
  const [uploadedImages, setUploadedImages] = useState([]);
  const [staffUser, setStaffUser] = useState(null); // Eingeloggter Mitarbeiter
  const [availableTags, setAvailableTags] = useState([]); // Verfügbare Tags für Staff
  const [selectedTags, setSelectedTags] = useState([]); // Ausgewählte Tags
  const [selectedCategory, setSelectedCategory] = useState(''); // Ausgewählte Kategorie
  const [notifyExternal, setNotifyExternal] = useState(false); // Checkbox "auch an Extern senden"
  
  // Kategorien für Mitarbeiter-Formular
  const categories = [
    { id: 'reparatur', name: 'Reparatur', icon: '🔧' },
    { id: 'reinigung', name: 'Reinigung', icon: '🧹' },
    { id: 'handwerker', name: 'Handwerker', icon: '👷' },
    { id: 'eigentuemer', name: 'Eigentümer', icon: '🏠' },
    { id: 'sonstiges', name: 'Sonstiges', icon: '📝' }
  ];

  // Bezeichnung der externen Benachrichtigungs-Checkbox je Kategorie
  const getExternalCheckboxLabel = (cat) => {
    if (['reparatur', 'eigentuemer', 'sonstiges'].includes(cat)) return 'Auch an Eigentümer senden';
    if (cat === 'reinigung') return 'Auch an Reinigungsfirma senden';
    if (cat === 'handwerker') return 'Auch an Handwerker senden';
    return null;
  };

  // Kategorie-Auswahl mit Checkbox-Reset
  const handleCategorySelect = (catId) => {
    setSelectedCategory(catId);
    setNotifyExternal(false); // Checkbox immer zurücksetzen bei Kategorie-Wechsel
  };
  
  // Form State
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'normal',
    reporter_name: '',
    reporter_email: '',
    booking_number: ''
  });

  // Property laden und Staff-Modus prüfen
  useEffect(() => {
    const loadProperty = async () => {
      try {
        const response = await axios.get(`${API}/guest/property/${propertyId}`);
        setProperty(response.data);
        
        // Wenn Staff-Modus (nach Login-Redirect), direkt zum Formular
        if (staffMode) {
          // Versuche eingeloggten Benutzer zu laden
          try {
            const token = localStorage.getItem('token');
            if (token) {
              axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
              // Korrekter Endpoint: /me (nicht /auth/me)
              const userRes = await axios.get(`${API}/me`);
              setStaffUser(userRes.data);
              setIsGuestMode(false);
              
              // Tags für Mitarbeiter laden
              try {
                const tagsRes = await axios.get(`${API}/tags`);
                setAvailableTags(tagsRes.data.filter(t => t.is_active));
              } catch (e) {
                console.warn('Tags load failed:', e);
              }
              
              setPageState('form');
              
              // URL bereinigen NICHT mehr nötig - staffMode bleibt in URL für Reload-Sicherheit
              // Bei Erfolg wird ohnehin zur Success-Seite navigiert
              return;
            }
          } catch (e) {
            console.warn('Staff user load failed:', e);
          }
        }
        
        setPageState('choice');
      } catch (err) {
        setErrorMessage('Das Objekt konnte nicht gefunden werden. Bitte prüfen Sie den QR-Code.');
        setPageState('error');
      }
    };
    
    loadProperty();
  }, [propertyId, staffMode]);

  // Login-Redirect (ohne React Router)
  // FIX: URL-basierter Return-Parameter statt sessionStorage
  // sessionStorage ist auf Android im QR-Scan-Kontext nicht zuverlässig
  const handleLoginRedirect = () => {
    const returnUrl = `/scan/${propertyId}?staff=1`;
    // Return-URL als URL-Parameter übergeben (Android-kompatibel)
    window.location.href = `/login?return=${encodeURIComponent(returnUrl)}`;
  };

  // Gast-Modus starten
  const handleContinueAsGuest = () => {
    setIsGuestMode(true);
    setPageState('form');
  };

  // Bild hinzufügen
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));
    
    setUploadedImages(prev => [...prev, ...newImages]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Bild entfernen
  const removeImage = (index) => {
    setUploadedImages(prev => {
      const newImages = [...prev];
      URL.revokeObjectURL(newImages[index].preview);
      newImages.splice(index, 1);
      return newImages;
    });
  };

  // Tag auswählen/abwählen
  const toggleTag = (tag) => {
    setSelectedTags(prev => {
      const isSelected = prev.some(t => t.id === tag.id);
      if (isSelected) {
        return prev.filter(t => t.id !== tag.id);
      } else {
        return [...prev, tag];
      }
    });
  };

  // Automatischen Textentwurf basierend auf Tags generieren
  const generateTextFromTags = () => {
    if (selectedTags.length === 0) return;
    
    // Titel aus Tags generieren
    const titleFragments = selectedTags
      .filter(t => t.title_fragment)
      .map(t => t.title_fragment);
    
    if (titleFragments.length > 0 && !formData.title) {
      const generatedTitle = titleFragments.join(' / ');
      setFormData(prev => ({ ...prev, title: generatedTitle }));
    }
    
    // Beschreibung aus Tags generieren
    const textFragments = selectedTags
      .filter(t => t.text_fragment)
      .map(t => t.text_fragment);
    
    if (textFragments.length > 0) {
      const generatedText = textFragments.join('\n\n');
      setFormData(prev => ({ 
        ...prev, 
        description: prev.description 
          ? prev.description + '\n\n' + generatedText 
          : generatedText 
      }));
    }
  };

  // Tags nach Kategorie filtern
  const getTagsForCategory = (category) => {
    if (!category) return availableTags.filter(t => !t.category);
    return availableTags.filter(t => t.category === category);
  };

  // Formular absenden
  const handleSubmit = async (e) => {
    e.preventDefault();
    setPageState('submitting');
    setErrorMessage('');
    
    try {
      // Beschreibung mit Buchungsnummer erweitern
      let fullDescription = formData.description;
      if (formData.booking_number) {
        fullDescription = `Buchungsnummer: ${formData.booking_number}\n\n${formData.description}`;
      }
      
      // Reporter-Name: Bei Staff-Modus den eingeloggten Benutzer verwenden
      const reporterName = staffUser ? staffUser.name : (formData.reporter_name || 'Gast');
      
      // Kategorie: Staff kann wählen, Gast bekommt 'reparatur' als Default
      const taskCategory = staffUser && selectedCategory ? selectedCategory : 'reparatur';
      
      const payload = {
        title: formData.title,
        description: fullDescription,
        category: taskCategory,
        priority: formData.priority,
        reporter_name: reporterName,
        reporter_email: staffUser ? '' : (formData.reporter_email || ''),
        booking_number: formData.booking_number || '',
        via_qr_code: true,
        created_by_staff: staffUser ? staffUser.username : null,
        // Staff-spezifische Felder
        tags: staffUser ? selectedTags.map(t => t.id) : [],
        // Externe Benachrichtigung (nur Staff, Gast immer false - Server erzwingt)
        notify_owner: staffUser && ['reparatur', 'eigentuemer', 'sonstiges'].includes(taskCategory) ? notifyExternal : false,
        notify_cleaning: staffUser && taskCategory === 'reinigung' ? notifyExternal : false,
        notify_contractor: staffUser && taskCategory === 'handwerker' ? notifyExternal : false,
      };
      
      const response = await axios.post(`${API}/guest/tasks/${propertyId}`, payload);
      const taskId = response.data.id;
      
      // Bilder hochladen
      if (uploadedImages.length > 0) {
        for (const img of uploadedImages) {
          const imgFormData = new FormData();
          imgFormData.append('file', img.file);
          imgFormData.append('task_id', taskId);
          
          try {
            await axios.post(`${API}/guest/tasks/${propertyId}/images`, imgFormData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          } catch (imgError) {
            console.error('Image upload error:', imgError);
          }
        }
      }
      
      // Finalize: Auto-Send E-Mail mit allen Bildern
      try {
        await axios.post(`${API}/guest/tasks/${taskId}/finalize`);
      } catch (finalizeError) {
        console.warn('Finalize/auto-send error (ignored):', finalizeError);
      }
      
      setPageState('success');
      
    } catch (err) {
      setErrorMessage(err.response?.data?.detail || 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
      setPageState('form');
    }
  };

  // Formular zurücksetzen
  const resetForm = () => {
    setPageState('form');
    setUploadedImages([]);
    setSelectedTags([]);
    setSelectedCategory('');
    setNotifyExternal(false);
    setFormData({
      title: '',
      description: '',
      priority: 'normal',
      reporter_name: '',
      reporter_email: '',
      booking_number: ''
    });
  };

  // Styles
  const styles = {
    page: {
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
      padding: '32px 16px',
      fontFamily: 'Arial, sans-serif'
    },
    container: {
      maxWidth: '480px',
      margin: '0 auto'
    },
    card: {
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      overflow: 'hidden',
      marginBottom: '16px'
    },
    cardContent: {
      padding: '24px'
    },
    header: {
      textAlign: 'center',
      marginBottom: '24px'
    },
    logo: {
      width: '64px',
      height: '64px',
      backgroundColor: '#0E4C92',
      borderRadius: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 16px auto',
      color: '#ffffff',
      fontSize: '24px'
    },
    title: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#1e293b',
      margin: '0 0 8px 0'
    },
    subtitle: {
      fontSize: '14px',
      color: '#64748b',
      margin: '0'
    },
    propertyCard: {
      backgroundColor: '#0E4C92',
      color: '#ffffff',
      borderRadius: '12px',
      padding: '20px',
      marginBottom: '16px'
    },
    propertyName: {
      fontSize: '18px',
      fontWeight: '600',
      margin: '0 0 4px 0'
    },
    propertyAddress: {
      fontSize: '14px',
      opacity: '0.9',
      margin: '0'
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '6px'
    },
    input: {
      width: '100%',
      height: '48px',
      fontSize: '16px',
      padding: '0 12px',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      backgroundColor: '#f9fafb',
      boxSizing: 'border-box',
      marginBottom: '16px'
    },
    textarea: {
      width: '100%',
      minHeight: '100px',
      fontSize: '16px',
      padding: '12px',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      backgroundColor: '#f9fafb',
      boxSizing: 'border-box',
      marginBottom: '16px',
      resize: 'vertical',
      fontFamily: 'inherit'
    },
    select: {
      width: '100%',
      height: '48px',
      fontSize: '16px',
      padding: '0 12px',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      backgroundColor: '#f9fafb',
      marginBottom: '16px'
    },
    button: {
      width: '100%',
      height: '52px',
      backgroundColor: '#0E4C92',
      color: '#ffffff',
      border: 'none',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    },
    buttonSecondary: {
      width: '100%',
      height: '52px',
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: '500',
      cursor: 'pointer',
      marginTop: '12px'
    },
    buttonDisabled: {
      backgroundColor: '#9ca3af',
      cursor: 'not-allowed'
    },
    errorBox: {
      backgroundColor: '#fef2f2',
      border: '1px solid #fecaca',
      borderRadius: '8px',
      padding: '12px',
      marginBottom: '16px'
    },
    errorText: {
      color: '#b91c1c',
      fontSize: '14px',
      margin: '0'
    },
    successIcon: {
      width: '80px',
      height: '80px',
      borderRadius: '50%',
      backgroundColor: '#d1fae5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 24px auto',
      fontSize: '40px'
    },
    successTitle: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#1e293b',
      textAlign: 'center',
      marginBottom: '16px'
    },
    successText: {
      fontSize: '16px',
      color: '#64748b',
      textAlign: 'center',
      marginBottom: '24px'
    },
    imageGrid: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px',
      marginBottom: '16px'
    },
    imagePreview: {
      width: '64px',
      height: '64px',
      borderRadius: '8px',
      overflow: 'hidden',
      position: 'relative',
      border: '1px solid #e5e7eb'
    },
    imageRemove: {
      position: 'absolute',
      top: '2px',
      right: '2px',
      width: '20px',
      height: '20px',
      backgroundColor: '#ef4444',
      color: '#ffffff',
      border: 'none',
      borderRadius: '50%',
      cursor: 'pointer',
      fontSize: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    addImageBtn: {
      width: '64px',
      height: '64px',
      border: '2px dashed #d1d5db',
      borderRadius: '8px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      color: '#9ca3af',
      backgroundColor: 'transparent'
    },
    divider: {
      display: 'flex',
      alignItems: 'center',
      margin: '16px 0'
    },
    dividerLine: {
      flex: 1,
      height: '1px',
      backgroundColor: '#e5e7eb'
    },
    dividerText: {
      padding: '0 12px',
      fontSize: '12px',
      color: '#9ca3af',
      textTransform: 'uppercase'
    },
    loadingSpinner: {
      width: '40px',
      height: '40px',
      border: '3px solid #e5e7eb',
      borderTopColor: '#0E4C92',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }
  };

  // LOADING
  if (pageState === 'loading') {
    return (
      <div style={{ ...styles.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={styles.loadingSpinner}></div>
          <p style={{ color: '#64748b', marginTop: '16px' }}>Lade...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  // ERROR (Objekt nicht gefunden)
  if (pageState === 'error' && !property) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.card}>
            <div style={{ ...styles.cardContent, textAlign: 'center', padding: '48px 24px' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>❌</div>
              <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1e293b', marginBottom: '8px' }}>
                Objekt nicht gefunden
              </h2>
              <p style={{ color: '#64748b', margin: '0' }}>{errorMessage}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // SUCCESS
  if (pageState === 'success') {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.card}>
            <div style={{ ...styles.cardContent, textAlign: 'center', padding: '48px 24px' }}>
              <div style={styles.successIcon}>✓</div>
              <h2 style={styles.successTitle}>Vielen Dank!</h2>
              <p style={styles.successText}>
                Ihre Meldung für <strong>{property?.name}</strong> wurde erfolgreich übermittelt.
                Wir kümmern uns darum.
              </p>
              <button
                onClick={resetForm}
                style={styles.button}
              >
                Weitere Meldung erstellen
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // CHOICE (Login oder Gast)
  if (pageState === 'choice') {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.header}>
            <div style={styles.logo}>📋</div>
            <h1 style={styles.title}>Aufgabe melden</h1>
          </div>

          {/* Property Info */}
          <div style={styles.propertyCard}>
            <p style={styles.propertyName}>{property?.name}</p>
            <p style={styles.propertyAddress}>📍 {property?.address}</p>
          </div>

          {/* Auswahl */}
          <div style={styles.card}>
            <div style={styles.cardContent}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b', marginBottom: '8px' }}>
                Schaden oder Hinweis melden
              </h3>
              <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '20px' }}>
                Als Gast können Sie direkt eine Meldung erstellen. Mitarbeitende melden sich bitte an.
              </p>
              
              <button
                onClick={handleContinueAsGuest}
                style={styles.button}
                data-testid="scan-guest-btn"
              >
                👤 Gast-Zugang
              </button>
              
              <div style={styles.divider}>
                <div style={styles.dividerLine}></div>
                <span style={styles.dividerText}>oder</span>
                <div style={styles.dividerLine}></div>
              </div>
              
              <button
                onClick={handleLoginRedirect}
                style={styles.buttonSecondary}
                data-testid="scan-login-btn"
              >
                🔐 Mitarbeitenden-Zugang
              </button>
              
              <p style={{ fontSize: '12px', color: '#9ca3af', textAlign: 'center', marginTop: '12px' }}>
                Bitte melden Sie sich an, wenn Sie als Mitarbeitender eine Aufgabe erfassen möchten.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // FORM
  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={{
            ...styles.logo,
            backgroundColor: staffUser ? '#059669' : '#0E4C92'
          }}>
            {staffUser ? '👷' : '📋'}
          </div>
          <h1 style={styles.title}>Neue Aufgabe</h1>
          <p style={styles.subtitle}>
            {staffUser ? `Mitarbeiter: ${staffUser.name}` : 'Gastmeldung'}
          </p>
        </div>

        {/* Property Info */}
        <div style={styles.propertyCard}>
          <p style={styles.propertyName}>{property?.name}</p>
          <p style={styles.propertyAddress}>📍 {property?.address}</p>
        </div>

        {/* Form */}
        <div style={styles.card}>
          <div style={styles.cardContent}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
              {staffUser ? 'Aufgabe erfassen' : 'Aufgabe beschreiben'}
            </h3>
            
            {errorMessage && (
              <div style={styles.errorBox}>
                <p style={styles.errorText}>{errorMessage}</p>
              </div>
            )}
            
            <form onSubmit={handleSubmit}>
              
              {/* ========== MITARBEITER-SPEZIFISCH: Kategorie ========== */}
              {staffUser && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={styles.label}>Kategorie</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {categories.map(cat => (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => handleCategorySelect(cat.id)}
                        style={{
                          padding: '10px 16px',
                          borderRadius: '8px',
                          border: selectedCategory === cat.id ? '2px solid #059669' : '1px solid #e5e7eb',
                          backgroundColor: selectedCategory === cat.id ? '#ecfdf5' : '#f9fafb',
                          color: selectedCategory === cat.id ? '#059669' : '#374151',
                          fontWeight: selectedCategory === cat.id ? '600' : '400',
                          cursor: 'pointer',
                          fontSize: '14px',
                          transition: 'all 0.2s'
                        }}
                        data-testid={`scan-category-${cat.id}`}
                      >
                        {cat.icon} {cat.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* ========== MITARBEITER-SPEZIFISCH: Tags ========== */}
              {staffUser && availableTags.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={styles.label}>
                    Tags auswählen
                    <span style={{ fontWeight: 'normal', color: '#9ca3af', marginLeft: '8px' }}>
                      (für automatischen Textentwurf)
                    </span>
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                    {(selectedCategory ? getTagsForCategory(selectedCategory) : availableTags.slice(0, 12)).map(tag => {
                      const isSelected = selectedTags.some(t => t.id === tag.id);
                      return (
                        <button
                          key={tag.id}
                          type="button"
                          onClick={() => toggleTag(tag)}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '16px',
                            border: isSelected ? 'none' : '1px solid #e5e7eb',
                            backgroundColor: isSelected ? tag.color : '#ffffff',
                            color: isSelected ? '#ffffff' : '#374151',
                            fontWeight: isSelected ? '500' : '400',
                            cursor: 'pointer',
                            fontSize: '13px',
                            transition: 'all 0.2s'
                          }}
                          data-testid={`scan-tag-${tag.id}`}
                        >
                          {tag.name}
                        </button>
                      );
                    })}
                  </div>
                  
                  {/* Textentwurf generieren Button */}
                  {selectedTags.length > 0 && (
                    <button
                      type="button"
                      onClick={generateTextFromTags}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '6px',
                        border: '1px solid #059669',
                        backgroundColor: '#ffffff',
                        color: '#059669',
                        fontWeight: '500',
                        cursor: 'pointer',
                        fontSize: '13px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                      data-testid="scan-generate-text-btn"
                    >
                      ✨ Textentwurf generieren ({selectedTags.length} Tags)
                    </button>
                  )}
                </div>
              )}

              <label style={styles.label}>Titel / Kurzbeschreibung *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder={staffUser ? "Automatisch aus Tags oder manuell eingeben" : "z.B. Wasserhahn tropft"}
                required
                disabled={pageState === 'submitting'}
                style={styles.input}
                data-testid="scan-title-input"
              />

              <label style={styles.label}>Beschreibung *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={staffUser ? "Automatisch aus Tags oder manuell eingeben" : "Beschreiben Sie das Problem oder Ihr Anliegen ausführlich..."}
                required
                disabled={pageState === 'submitting'}
                style={styles.textarea}
                data-testid="scan-description-input"
              />

              {/* Fotos */}
              <label style={styles.label}>Fotos hinzufügen</label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                capture="environment"
                onChange={handleImageSelect}
                style={{ display: 'none' }}
                data-testid="scan-image-input"
              />
              <div style={styles.imageGrid}>
                {uploadedImages.map((img, index) => (
                  <div key={index} style={styles.imagePreview}>
                    <img
                      src={img.preview}
                      alt={img.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      style={styles.imageRemove}
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  style={styles.addImageBtn}
                  data-testid="scan-add-image-btn"
                >
                  📷
                  <span style={{ fontSize: '10px' }}>Foto</span>
                </button>
              </div>

              <label style={styles.label}>Dringlichkeit</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                disabled={pageState === 'submitting'}
                style={styles.select}
                data-testid="scan-priority-select"
              >
                <option value="normal">Normal</option>
                <option value="hoch">Dringend</option>
              </select>

              {/* Kontaktdaten - nur für Gäste anzeigen */}
              {isGuestMode && (
                <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '16px', marginTop: '8px' }}>
                  <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '16px' }}>
                    Optional: Ihre Kontaktdaten für Rückfragen
                  </p>
                  
                  <label style={styles.label}>Buchungsnummer</label>
                  <input
                    type="text"
                    value={formData.booking_number}
                    onChange={(e) => setFormData({ ...formData, booking_number: e.target.value })}
                    placeholder="z.B. BK-12345"
                    disabled={pageState === 'submitting'}
                    style={styles.input}
                    data-testid="scan-booking-number-input"
                  />

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={styles.label}>Ihr Name</label>
                      <input
                        type="text"
                        value={formData.reporter_name}
                        onChange={(e) => setFormData({ ...formData, reporter_name: e.target.value })}
                        placeholder="Max Mustermann"
                        disabled={pageState === 'submitting'}
                        style={{ ...styles.input, marginBottom: '0' }}
                        data-testid="scan-name-input"
                      />
                    </div>
                    <div>
                      <label style={styles.label}>Ihre E-Mail</label>
                      <input
                        type="email"
                        value={formData.reporter_email}
                        onChange={(e) => setFormData({ ...formData, reporter_email: e.target.value })}
                        placeholder="max@example.de"
                        disabled={pageState === 'submitting'}
                        style={{ ...styles.input, marginBottom: '0' }}
                        data-testid="scan-email-input"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* ========== MITARBEITER-SPEZIFISCH: Externe Benachrichtigung ========== */}
              {staffUser && selectedCategory && getExternalCheckboxLabel(selectedCategory) && (
                <div style={{
                  marginBottom: '16px',
                  padding: '14px 16px',
                  backgroundColor: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px'
                }}>
                  <input
                    type="checkbox"
                    id="notify-external"
                    checked={notifyExternal}
                    onChange={(e) => setNotifyExternal(e.target.checked)}
                    disabled={pageState === 'submitting'}
                    style={{ marginTop: '2px', width: '16px', height: '16px', cursor: 'pointer', accentColor: '#059669' }}
                    data-testid="scan-notify-external-checkbox"
                  />
                  <label htmlFor="notify-external" style={{ cursor: 'pointer', fontSize: '14px', color: '#166534', lineHeight: '1.4' }}>
                    <strong>{getExternalCheckboxLabel(selectedCategory)}</strong>
                    <br />
                    <span style={{ fontWeight: 'normal', color: '#4b7c63', fontSize: '12px' }}>
                      Mail geht immer an info@gb-sylt.de. Mit Haken: Kopie als CC an externen Empfänger.
                    </span>
                  </label>
                </div>
              )}

              <button
                style={{
                  ...styles.button,
                  ...(pageState === 'submitting' ? styles.buttonDisabled : {}),
                  marginTop: '24px',
                  backgroundColor: staffUser ? '#059669' : '#0E4C92'
                }}
                data-testid="scan-submit-btn"
              >
                {pageState === 'submitting' ? '⏳ Wird gesendet...' : '📤 Aufgabe erstellen'}
              </button>
            </form>
          </div>
        </div>

        <p style={{ textAlign: 'center', fontSize: '12px', color: '#9ca3af' }}>
          GB Objekt- & Aufgabenmanagement • Sylt
        </p>
        
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
};

export default ScanPage;
